/* eslint-disable max-lines */
import { useEffect, useMemo, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { formatDistanceToNow } from "date-fns"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import type { UserDocument } from "@/api/models"
import type { LucideIcon } from "@/components/icons"
import type { ArtifactFilter } from "@/features/artifacts/utils/artifact-type-meta"

import {
  ChartColumn,
  Eye,
  FileChartColumn,
  FileText,
  List,
  MoreHorizontal,
  Plus,
  Presentation,
  Share2,
  Star,
  Table,
  Trash2,
  Users,
} from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Spinner } from "@/components/ui/spinner"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import {
  artifactTypeFilterKey,
  documentMatchesFilter,
} from "@/features/artifacts/utils/artifact-type-meta"
import { sanitizeSnippetForPreview } from "@/features/chat/utils/document-reference-preview-utils"
import { DocumentShareDialog } from "@/features/documents/components/document-share-dialog"
import { useDateLocale } from "@/hooks/use-date-locale"
import { useCreateDocument, useDeleteDocument, useDocuments } from "@/hooks/use-documents"
import { useToggleFavoriteDocument, useUserSettings } from "@/hooks/use-user-settings"
import { queryKeys } from "@/lib/query-keys"
import { cn } from "@/lib/utils"

function getRoleLabel(
  t: (key: string, options?: { defaultValue: string }) => string,
  role?: UserDocument["currentUserRole"]
) {
  if (role === "owner") {
    return t("sharing.role_owner", { defaultValue: "Owner" })
  }
  if (role === "reader") {
    return t("sharing.role_reader", { defaultValue: "Reader" })
  }
  return null
}

type DocumentsLayoutMode = "preview" | "dense"

const DOCUMENTS_LAYOUT_MODE_STORAGE_KEY = "documents-landing-layout-mode"

function getInitialLayoutMode(): DocumentsLayoutMode {
  if (typeof window === "undefined") {
    return "preview"
  }

  const storedMode = window.localStorage.getItem(DOCUMENTS_LAYOUT_MODE_STORAGE_KEY)
  if (storedMode === "preview" || storedMode === "dense") {
    return storedMode
  }

  return "preview"
}

const ARTIFACT_FILTERS: { value: ArtifactFilter; labelKey: string }[] = [
  { value: "all", labelKey: "documents.filter_all" },
  { value: "document", labelKey: "documents.filter_document" },
  { value: "report", labelKey: "documents.filter_report" },
  { value: "chart", labelKey: "documents.filter_chart" },
  { value: "sheet", labelKey: "documents.filter_sheet" },
  { value: "slides", labelKey: "documents.filter_slides" },
]

const FILTER_KEY_TO_ICON: Record<Exclude<ArtifactFilter, "all">, LucideIcon> = {
  document: FileText,
  report: FileChartColumn,
  chart: ChartColumn,
  sheet: Table,
  slides: Presentation,
}

function getDocumentTypeIcon(document: UserDocument): LucideIcon {
  return FILTER_KEY_TO_ICON[artifactTypeFilterKey(document)]
}

export function DocumentsLandingPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dateLocale = useDateLocale()
  const queryClient = useQueryClient()
  const { data: documents, isLoading } = useDocuments()
  const { data: userSettings } = useUserSettings()
  const createDocument = useCreateDocument()
  const deleteDocument = useDeleteDocument()
  const toggleFavoriteDocument = useToggleFavoriteDocument()

  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.documents() })
  }, [queryClient])
  const [sharingDocument, setSharingDocument] = useState<UserDocument | null>(null)
  const [deletingDocument, setDeletingDocument] = useState<UserDocument | null>(null)
  const [openMenuDocumentId, setOpenMenuDocumentId] = useState<string | null>(null)
  const [layoutMode, setLayoutMode] = useState<DocumentsLayoutMode>(getInitialLayoutMode)
  const [activeFilter, setActiveFilter] = useState<ArtifactFilter>("all")
  const favoriteDocumentIds = useMemo(
    () => new Set(userSettings?.data.favorite_documents ?? []),
    [userSettings?.data.favorite_documents]
  )

  const filteredDocuments = useMemo(
    () => (documents ?? []).filter((document) => documentMatchesFilter(document, activeFilter)),
    [documents, activeFilter]
  )

  const sortedDocuments = useMemo(() => {
    return [...filteredDocuments].sort((a, b) => {
      const aTime = new Date(a.updated ?? 0).getTime()
      const bTime = new Date(b.updated ?? 0).getTime()
      return bTime - aTime
    })
  }, [filteredDocuments])
  const favoriteDocuments = useMemo(
    () => sortedDocuments.filter((document) => favoriteDocumentIds.has(document.id)),
    [favoriteDocumentIds, sortedDocuments]
  )
  const nonFavoriteDocuments = useMemo(
    () => sortedDocuments.filter((document) => !favoriteDocumentIds.has(document.id)),
    [favoriteDocumentIds, sortedDocuments]
  )

  const now = Date.now()
  const twoDaysMs = 2 * 24 * 60 * 60 * 1000
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000

  const recentDocuments = useMemo(
    () =>
      nonFavoriteDocuments.filter((document) => {
        const updatedAt = new Date(document.updated ?? 0).getTime()
        return now - updatedAt <= twoDaysMs
      }),
    [nonFavoriteDocuments, now, twoDaysMs]
  )

  const thisWeekDocuments = useMemo(
    () =>
      nonFavoriteDocuments.filter((document) => {
        const updatedAt = new Date(document.updated ?? 0).getTime()
        const age = now - updatedAt
        return age > twoDaysMs && age <= sevenDaysMs
      }),
    [nonFavoriteDocuments, now, sevenDaysMs, twoDaysMs]
  )

  const olderDocuments = useMemo(
    () =>
      nonFavoriteDocuments.filter((document) => {
        const updatedAt = new Date(document.updated ?? 0).getTime()
        return now - updatedAt > sevenDaysMs
      }),
    [nonFavoriteDocuments, now, sevenDaysMs]
  )

  const documentSections = useMemo(() => {
    const sections = []
    if (favoriteDocuments.length > 0) {
      sections.push({
        id: "favorites",
        title: t("documents.section_favorites", { defaultValue: "Favorites" }),
        items: favoriteDocuments,
      })
    }
    sections.push(
      {
        id: "recent",
        title: t("documents.section_recent", { defaultValue: "Recent" }),
        items: recentDocuments,
      },
      {
        id: "this-week",
        title: t("documents.section_this_week", { defaultValue: "This week" }),
        items: thisWeekDocuments,
      },
      {
        id: "older",
        title: t("documents.section_older", { defaultValue: "Older" }),
        items: olderDocuments,
      }
    )

    return sections
  }, [favoriteDocuments, olderDocuments, recentDocuments, t, thisWeekDocuments])

  const handleCreateDocument = () => {
    createDocument.mutate(
      {
        title: t("documents.untitled", { defaultValue: "Untitled document" }),
        content: "",
        contentFormat: "markdown",
      },
      {
        onSuccess: (created) => {
          void navigate(`/documents/${created.id}`)
        },
      }
    )
  }

  const handleLayoutModeChange = (mode: DocumentsLayoutMode) => {
    setLayoutMode(mode)
    if (typeof window !== "undefined") {
      window.localStorage.setItem(DOCUMENTS_LAYOUT_MODE_STORAGE_KEY, mode)
    }
  }

  const handleConfirmDelete = () => {
    if (!deletingDocument || deleteDocument.isPending) {
      return
    }

    deleteDocument.mutate(deletingDocument.id, {
      onSuccess: () => {
        if (sharingDocument?.id === deletingDocument.id) {
          setSharingDocument(null)
        }
        setDeletingDocument(null)
      },
    })
  }

  const documentsContent = (() => {
    if (isLoading) {
      return (
        <div className="bg-card rounded-xl border">
          <div className="flex justify-center py-10">
            <Spinner className="size-8" />
          </div>
        </div>
      )
    }

    if (sortedDocuments.length === 0) {
      const hasAnyDocuments = (documents?.length ?? 0) > 0
      return (
        <div className="bg-card rounded-xl border">
          <div className="text-muted-foreground flex flex-col items-center gap-2 py-12 text-sm">
            <FileText className="size-8" />
            {hasAnyDocuments && activeFilter !== "all"
              ? t("documents.no_matches", { defaultValue: "No documents found" })
              : t("documents.empty", { defaultValue: "No documents yet" })}
          </div>
        </div>
      )
    }

    return documentSections
      .filter((section) => section.items.length > 0)
      .map((section) => (
        <section key={section.id} className="bg-card rounded-xl border">
          <header className="border-b px-4 py-3 sm:px-5">
            <h2 className="text-foreground text-sm font-medium">{section.title}</h2>
          </header>
          <div className="divide-y">
            {section.items.map((document) => {
              const isFavorite = favoriteDocumentIds.has(document.id)
              const roleLabel = getRoleLabel(t, document.currentUserRole)
              const participantCount = document.participants?.length ?? 0
              const relativeUpdatedLabel = document.updated
                ? formatDistanceToNow(new Date(document.updated), {
                    addSuffix: true,
                    locale: dateLocale,
                  })
                : t("documents.just_now", { defaultValue: "just now" })
              const contentPreview =
                (document.content || "").trim() ||
                t("documents.empty_content", { defaultValue: "Empty document" })
              const sanitizedContentPreview = sanitizeSnippetForPreview(contentPreview)
              const TypeIcon = getDocumentTypeIcon(document)

              return (
                <div
                  key={document.id}
                  className={cn(
                    "group/document-row hover:bg-muted/40 flex items-start gap-2 px-4 transition-colors sm:px-5",
                    layoutMode === "preview" ? "py-3" : "py-2.5"
                  )}
                >
                  <button
                    type="button"
                    className={cn(
                      "flex min-w-0 flex-1 text-left",
                      layoutMode === "preview" ? "items-start gap-3 pr-2" : "flex-col gap-1"
                    )}
                    onClick={() => navigate(`/documents/${document.id}`)}
                  >
                    {layoutMode === "preview" && (
                      <div
                        aria-hidden="true"
                        className="hidden h-[106px] w-[74px] shrink-0 sm:flex sm:items-center sm:justify-center"
                      >
                        <div className="border-border/50 bg-background relative h-[102px] w-[70px] overflow-hidden rounded-[4px] border p-1.5 shadow-xs">
                          <span
                            aria-hidden="true"
                            className="bg-muted/45 border-border/45 absolute top-0 right-0 h-2.5 w-2.5 rounded-bl-[3px] border-b border-l"
                          />
                          <p className="line-clamp-2 text-[4px] leading-[1.2] font-semibold tracking-tight">
                            {document.title}
                          </p>
                          <p className="text-muted-foreground mt-1 overflow-hidden text-[3px] leading-[1.2]">
                            {sanitizedContentPreview || document.title}
                          </p>
                        </div>
                      </div>
                    )}
                    <div
                      className={cn(
                        "min-w-0 flex-1",
                        layoutMode === "preview" ? "space-y-1.5" : "space-y-1"
                      )}
                    >
                      <div className="flex min-w-0 items-center gap-1.5">
                        <TypeIcon
                          aria-hidden="true"
                          className="text-muted-foreground size-4 shrink-0"
                        />
                        <p className="text-foreground truncate text-sm font-medium">
                          {document.title}
                        </p>
                      </div>
                      <div className="text-muted-foreground flex items-center gap-1 text-xs">
                        <span>{t("documents.updated", { defaultValue: "Updated" })}</span>
                        <span aria-hidden="true">&bull;</span>
                        <span>{relativeUpdatedLabel}</span>
                      </div>
                      {layoutMode === "preview" && (roleLabel || participantCount > 1) && (
                        <div className="flex min-w-0 items-center gap-2">
                          {roleLabel && (
                            <span
                              className={cn(
                                "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium",
                                document.currentUserRole === "owner"
                                  ? "bg-warning/30 text-warning-ink"
                                  : "border-foreground bg-accent text-accent-foreground border"
                              )}
                            >
                              {roleLabel}
                            </span>
                          )}
                          {participantCount > 1 && (
                            <span className="bg-muted text-muted-foreground inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium">
                              <Users className="size-3" />
                              {participantCount}
                            </span>
                          )}
                        </div>
                      )}
                      {layoutMode === "preview" && (
                        <p className="text-muted-foreground line-clamp-2 text-xs leading-relaxed">
                          {sanitizedContentPreview || contentPreview}
                        </p>
                      )}
                    </div>
                  </button>

                  <div className="flex shrink-0 items-start gap-1">
                    <DropdownMenu
                      open={openMenuDocumentId === document.id}
                      onOpenChange={(isOpen) => {
                        setOpenMenuDocumentId(isOpen ? document.id : null)
                      }}
                    >
                      <DropdownMenuTrigger asChild>
                        <Button
                          aria-label={t("documents.more_actions", { defaultValue: "More actions" })}
                          size="icon"
                          variant="ghost"
                          className={cn(
                            "h-8 w-8 shrink-0 opacity-0 transition-opacity duration-150",
                            "group-hover/document-row:opacity-100",
                            openMenuDocumentId === document.id && "opacity-100"
                          )}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <MoreHorizontal className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          className="gap-2"
                          disabled={toggleFavoriteDocument.isPending}
                          onClick={(event) => {
                            event.stopPropagation()
                            toggleFavoriteDocument.mutate({ documentId: document.id })
                          }}
                        >
                          <Star className="size-4" />
                          {isFavorite
                            ? t("documents.unfavorite", { defaultValue: "Remove favorite" })
                            : t("documents.favorite", { defaultValue: "Mark as favorite" })}
                        </DropdownMenuItem>
                        {document.currentUserRole === "owner" && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="gap-2"
                              onClick={() => setSharingDocument(document)}
                            >
                              <Share2 className="size-4" />
                              {t("sharing.share", { defaultValue: "Share" })}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive gap-2"
                              disabled={deleteDocument.isPending}
                              onClick={() => setDeletingDocument(document)}
                            >
                              <Trash2 className="size-4" />
                              {t("canvas.delete_document", { defaultValue: "Delete document" })}
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      ))
  })()

  return (
    <div className="mx-auto flex h-full w-full max-w-5xl flex-col gap-5 px-4 py-5 sm:px-6 sm:py-8">
      <header className="flex items-center justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-foreground text-2xl font-semibold">
            {t("documents.title", { defaultValue: "Documents" })}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("documents.subtitle", {
              defaultValue: "Create and edit standalone documents across chats.",
            })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ToggleGroup
            type="single"
            value={layoutMode}
            onValueChange={(value) => {
              if (value === "preview" || value === "dense") {
                handleLayoutModeChange(value)
              }
            }}
          >
            <ToggleGroupItem
              aria-label={t("documents.preview_mode", { defaultValue: "Preview mode" })}
              className="h-9 w-9"
              value="preview"
            >
              <Eye className="size-4" />
            </ToggleGroupItem>
            <ToggleGroupItem
              aria-label={t("documents.dense_mode", { defaultValue: "Dense mode" })}
              className="h-9 w-9"
              value="dense"
            >
              <List className="size-4" />
            </ToggleGroupItem>
          </ToggleGroup>
          <Button className="gap-2" onClick={handleCreateDocument}>
            <Plus className="size-4" />
            {t("documents.new", { defaultValue: "New document" })}
          </Button>
        </div>
      </header>

      <ToggleGroup
        aria-label={t("documents.filter_label", { defaultValue: "Filter by type" })}
        className="flex flex-wrap justify-start gap-2"
        type="single"
        value={activeFilter}
        onValueChange={(value) => {
          if (value) {
            setActiveFilter(value as ArtifactFilter)
          }
        }}
      >
        {ARTIFACT_FILTERS.map((filter) => (
          <ToggleGroupItem
            key={filter.value}
            className="h-8 rounded-full px-3 text-xs font-medium"
            value={filter.value}
          >
            {t(filter.labelKey)}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      <div className="flex flex-col gap-4">{documentsContent}</div>

      {sharingDocument && (
        <DocumentShareDialog
          currentUserRole={sharingDocument.currentUserRole}
          documentId={sharingDocument.id}
          open={!!sharingDocument}
          ownerUserId={sharingDocument.ownerUserId}
          onOpenChange={(open) => {
            if (!open) {
              setSharingDocument(null)
            }
          }}
        />
      )}

      <AlertDialog
        open={!!deletingDocument}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingDocument(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("canvas.confirm_delete_document", { defaultValue: "Delete document?" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("documents.delete_confirmation", {
                defaultValue:
                  "This action cannot be undone. This document will be permanently removed.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", { defaultValue: "Cancel" })}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteDocument.isPending || !deletingDocument}
              onClick={handleConfirmDelete}
            >
              {deleteDocument.isPending
                ? t("common.deleting", { defaultValue: "Deleting..." })
                : t("canvas.delete_document", { defaultValue: "Delete document" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
